/**
 * A time, chosen from a typed box or a dropdown of quarter hours — never the
 * platform's own `<input type="time">`.
 *
 * `<input type="time">` was what every console time field used to be, and
 * what it renders is not ours: three blue-highlighted spinner columns in the
 * platform's own palette, over the top of the card, in nothing like the
 * console's vocabulary. On iOS WKWebView it is worse than off-brand — it is a
 * spinner wheel with no typed-entry path at all. This is the same control the
 * console's choice fields already use — the value with a chevron, a panel
 * under it — with times in it.
 *
 * The typed box at the top of the panel is the primary path; the list below
 * it (quarter hours — see `backend/src/shared/timeOfDay.ts`) is the fast one,
 * and opens scrolled to the value the field already holds rather than at
 * midnight.
 *
 * This is the ONE time control for the console: `ReviewQueue`'s review cards
 * and `ItemDetail`'s field editor both render it (extracted from
 * `ReviewQueue`'s original `ReviewTimeField` in
 * `refactor(console): every time field takes typed entry`), so the two
 * places never drift into two different pickers with two different bugs.
 *
 * Each caller already has its own `data-testid` convention for the field it
 * is rendering (`ReviewQueue`'s is `review-field-editor-${fieldKey}`,
 * `ItemDetail`'s is `edit-field-${field.key}`) — rather than this component
 * inventing a third, the exact id for the trigger control is passed in as
 * `testId`.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { C } from "../tokens";
import {
  displayTime,
  parseTypedTime,
  TIME_STEPS,
} from "../../../../backend/src/shared/timeOfDay";

/** Every option's `onMouseDown`. A pick moves focus at `mousedown` — before
 *  `click` — which blurs the autofocused typed box, whose commit closes the
 *  panel and unmounts the option before its own click arrives. LED-375: every
 *  option pick was a no-op while typing still worked. Keeping focus in the
 *  typed box lets the click land; the option's commit then closes the panel. */
const keepTypedBoxFocused = (event: ReactMouseEvent) => event.preventDefault();

export interface TimeFieldProps {
  fieldKey: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The exact `data-testid` for the trigger control — see file doc. */
  testId: string;
  /** Inside a review field pill: the value only, left-aligned; the pill
   *  carries the clock glyph. */
  pill?: boolean;
}

export function TimeField({ fieldKey, label, value, onChange, testId, pill = false }: TimeFieldProps) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  // Set only when a blur/Enter committed text that `parseTypedTime` could not
  // read. The previous value is never touched — `commitTyped` simply does
  // not call `onChange` — but the reader needs to be TOLD that, or a box
  // that silently keeps showing their unparsed text (or worse, snaps back to
  // the old value) reads as "nothing happened" rather than "that didn't
  // parse, try again".
  const [rejected, setRejected] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const shown = displayTime(value);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  // Opening on the value the field HOLDS: a 96-row list that always opened at
  // midnight would make every edit start with the same scroll.
  useEffect(() => {
    if (!open) return;
    setTyped(shown ?? "");
    setRejected(false);
    const selected = listRef.current?.querySelector<HTMLElement>('[data-selected="true"]');
    selected?.scrollIntoView({ block: "center" });
    // `shown` is read once, on opening — a value changed while the panel is
    // up is the reader's own doing and must not scroll the list under them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commitTyped = () => {
    const parsed = parseTypedTime(typed);
    if (parsed === null) {
      // Empty box on blur is just "cleared, then changed my mind" — that is
      // not a rejection. Anything else that fails to parse is.
      setRejected(typed.trim() !== "");
      return;
    }
    onChange(parsed);
    setRejected(false);
    setOpen(false);
  };

  return (
    <div
      ref={ref}
      style={{ position: "relative", flex: pill ? "none" : 1, minWidth: 0, zIndex: open ? 20 : 1 }}
    >
      <button
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={testId}
        onClick={() => setOpen((up) => !up)}
        style={pill ? {
          display: "flex",
          alignItems: "center",
          padding: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          color: shown ? C.ink : C.sub2,
          font: "inherit",
          fontSize: 14,
          fontWeight: shown ? 600 : 500,
          cursor: "pointer",
        } : {
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 7,
          padding: "5px 7px",
          border: "1px solid transparent",
          borderRadius: 8,
          outline: "none",
          background: "transparent",
          color: shown ? C.ink : C.sub2,
          font: "inherit",
          fontSize: 16.67,
          fontWeight: 650,
          lineHeight: 1.45,
          letterSpacing: "-0.15px",
          cursor: "pointer",
        }}
      >
        <span>{shown ?? "Not set"}</span>
        {!pill && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="8.6" stroke={C.sub} strokeWidth="1.7" />
          <path
            d="M12 7.6V12l3 1.9"
            stroke={C.sub}
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`${label} picker`}
          style={{
            position: "absolute",
            ...(pill ? { left: -40 } : { right: 0 }),
            top: "calc(100% + 7px)",
            width: 188,
            padding: 5,
            border: `1px solid ${C.line2}`,
            borderRadius: 12,
            background: C.surface,
            boxShadow: "0 14px 30px rgba(22, 18, 10, 0.16)",
            animation: "ledai-review-dropdown-in 140ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        >
          <input
            autoFocus
            aria-label={`${label}, typed`}
            aria-invalid={rejected}
            data-testid={`${testId}-typed`}
            value={typed}
            placeholder="7:52 PM"
            onChange={(event) => {
              setTyped(event.target.value);
              setRejected(false);
            }}
            onInput={(event) => {
              setTyped((event.target as HTMLInputElement).value);
              setRejected(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitTyped();
              }
              if (event.key === "Escape") setOpen(false);
            }}
            onBlur={commitTyped}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "7px 9px",
              border: `1px solid ${rejected ? C.red : C.line2}`,
              borderRadius: 9,
              outline: "none",
              background: C.canvas,
              color: C.ink,
              font: "inherit",
              fontSize: 15.52,
              fontWeight: 600,
            }}
          />

          {rejected && (
            <p
              role="alert"
              data-testid={`${testId}-error`}
              style={{
                margin: "5px 2px 0",
                fontSize: 13.23,
                lineHeight: 1.4,
                color: C.red,
              }}
            >
              Not a time — try 7:52 PM or 19:52. Kept {shown ?? "Not set"}.
            </p>
          )}

          <div
            ref={listRef}
            role="listbox"
            aria-label={`${label} options`}
            style={{ maxHeight: 208, overflowY: "auto", marginTop: 5, scrollbarWidth: "thin" }}
          >
            <button
              type="button"
              role="option"
              aria-selected={value === ""}
              data-selected={value === "" ? "true" : undefined}
              onMouseDown={keepTypedBoxFocused}
              onClick={() => {
                onChange("");
                setRejected(false);
                setOpen(false);
              }}
              style={dropdownOptionStyle(value === "")}
            >
              Not set
            </button>
            {TIME_STEPS.map((step) => {
              const selected = step === value;
              return (
                <button
                  key={step}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-selected={selected ? "true" : undefined}
                  data-testid={`${testId}-option`}
                  onMouseDown={keepTypedBoxFocused}
                  onClick={() => {
                    onChange(step);
                    setRejected(false);
                    setOpen(false);
                  }}
                  style={dropdownOptionStyle(selected)}
                >
                  {displayTime(step)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function dropdownOptionStyle(selected: boolean): CSSProperties {
  return {
    display: "block",
    width: "100%",
    padding: "8px 10px",
    border: "none",
    borderRadius: 8,
    background: selected ? C.faint : "transparent",
    color: C.ink,
    textAlign: "left",
    font: "inherit",
    fontSize: 14.95,
    fontWeight: selected ? 650 : 500,
    cursor: "pointer",
  };
}
